import { Box, Typography } from '@mui/material';
import { type GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import ExcelJS from 'exceljs';
import { type MutableRefObject, useCallback, useMemo, useState } from 'react';
import { notDefined, whenDefined } from '@vy/null';
import { now, vyDate } from '@vy/utils';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { useFiltersParam } from '../../../../hooks/search-params/use-filters-param';
import { useGridColumnSettings } from '../../../../hooks/use-grid-column-settings';
import {
  useCommitmentVsUsageReport,
  useRefreshCommitmentVsUsageReport,
} from '../../../../hooks/requests/use-commitment-vs-usage-report';
import { useSearchWithSearchParams } from '../../../../hooks/use-search-with-search-params';
import { toastFromPromise } from '../../../../utils/vy-toasts';
import { formatDate } from '../../../../utils/date';
import { REPORT_TYPES } from '../../reports-page/reports-page-types/report-type';
import { buildCommitmentVsUsageExport } from '../../reports-page/reports-page-utils/commitment-vs-usage-report-utils/build-commitment-vs-usage-export';
import { ReportTable } from '../../reports-components/ReportTable';
import { useFilteredCommitmentVsUsage } from '../hooks/use-filtered-commitment-vs-usage';
import { useCommitmentVsUsageFilterFields } from '../hooks/use-commitment-vs-usage-filter-fields';
import { CommitmentVsUsageReportTable } from './CommitmentVsUsageReportTable/CommitmentVsUsageReportTable';
import { buildCommitmentVsUsageColumns } from './CommitmentVsUsageReportTable/CommitmentVsUsageReportTableColumns';
import { CommitmentVsUsageReportToolbar } from './CommitmentVsUsageReportToolbar';

const FILTERS_PARAM_NAME = 'commitmentVsUsageFilters';

export const CommitmentVsUsageReportContent: React.FC = () => {
  const apiRef = useGridApiRef();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchText, setSearchText] = useSearchWithSearchParams();
  const [fromDate, setFromDate] = useState<Date | null>(() =>
    vyDate().subtract(11, 'month').startOf('month').toDate(),
  );
  const [toDate, setToDate] = useState<Date | null>(() => vyDate().startOf('month').toDate());

  const period = useMemo(
    () => ({ startTime: fromDate ?? undefined, endTime: toDate ?? undefined }),
    [fromDate, toDate],
  );

  const { data: report } = useCommitmentVsUsageReport(period);
  const refreshReport = useRefreshCommitmentVsUsageReport(period);

  const filterFields = useCommitmentVsUsageFilterFields(report?.reportItems ?? []);
  const [rawFilters, setRawFilters] = useFiltersParam<FilterLineType>(FILTERS_PARAM_NAME, filterFields);
  const filters = useMemo(() => rawFilters ?? [], [rawFilters]);

  const handleFiltersChange = useCallback(
    (nextFilters: FilterLineType[]) => setRawFilters(nextFilters),
    [setRawFilters],
  );

  const filteredReport = useFilteredCommitmentVsUsage(report, filters, searchText);

  const months = report?.months ?? [];
  const columns = useMemo(() => buildCommitmentVsUsageColumns(months), [months]);

  const {
    isSettingsOpen,
    openSettings,
    closeSettings,
    columnSettings,
    handleColumnVisibilityChange,
  } = useGridColumnSettings(apiRef as MutableRefObject<GridApiCommunity>, columns);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await toastFromPromise(refreshReport(), {
      pending: 'Refreshing report...',
      success: 'Report refreshed',
      error: 'Failed to refresh report',
    }).finally(() => setIsRefreshing(false));
  };

  const handleExportToXlsx = async () => {
    if (notDefined(filteredReport)) {
      return undefined;
    }

    const fileName = `Commitment vs Usage (${formatDate(now(), 'DD.MM.YYYY')}).xlsx`;
    const visibleFields = new Set(
      apiRef.current?.getVisibleColumns().map((column) => column.field) ?? [],
    );
    const hiddenFields = new Set(
      columns.map((column) => column.field).filter((field) => !visibleFields.has(field)),
    );

    await toastFromPromise(
      (async () => {
        const workbook = new ExcelJS.Workbook();
        buildCommitmentVsUsageExport(workbook, filteredReport, hiddenFields);
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        URL.revokeObjectURL(url);
      })(),
      { pending: 'Downloading...', success: 'Download complete', error: 'Failed to download' },
    );
  };

  const reportGeneratedAtDate = whenDefined(report?.reportGeneratedAt, vyDate, null);
  const lastRefreshedLabel = reportGeneratedAtDate ? (
    <Typography variant="caption" sx={{
      color: "text.secondary"
    }}>
      Last updated: {reportGeneratedAtDate.format('MMM D, YYYY [at] h:mm A')}
    </Typography>
  ) : null;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '20px' }}>
          Commitment vs. Usage - Detailed
        </Typography>
        {lastRefreshedLabel}
      </Box>

      <ReportTable
        title="Commitment vs. Usage"
        hideTitle
        exportCsvFileName="Commitment vs Usage"
        reportCreationDate={report?.reportGeneratedAt ?? new Date()}
        reportType={REPORT_TYPES.COMMITMENT_VS_USAGE}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        onExportCsv={handleExportToXlsx}
        hideMenu
      >
        <CommitmentVsUsageReportToolbar
          onSearchChange={(value) => setSearchText(value)}
          searchDefaultValue={searchText ?? ''}
          fromDate={fromDate}
          onFromDateChange={setFromDate}
          toDate={toDate}
          onToDateChange={setToDate}
          filterFields={filterFields}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onExport={handleExportToXlsx}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          isColumnSettingsOpen={isSettingsOpen}
          onOpenColumnSettings={openSettings}
          onCloseColumnSettings={closeSettings}
          columnSettings={columnSettings}
          onColumnVisibilityChange={handleColumnVisibilityChange}
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <CommitmentVsUsageReportTable
            period={period}
            searchText={searchText}
            filters={filters}
            columns={columns}
            apiRef={apiRef as MutableRefObject<GridApiPro>}
          />
        </Box>
      </ReportTable>
    </>
  );
};
